import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  captureListState,
  LIST_RESTORE_STATE_KEY,
  buildDetailNavigationState,
} from '../utils/listReturnState';
import { buildListToDetailNavigationState } from '../utils/breadcrumbTrail';

export { buildDetailNavigationState, LIST_RESTORE_STATE_KEY, captureListState };

/**
 * بازیابی فیلتر لیست پس از بازگشت از جزئیات، و navigate به جزئیات با snapshot.
 *
 * @param {() => object} capture - تابعی که وضعیت فعلی لیست را برمی‌گرداند
 * @param {(restored: object) => void} applyRestored - اعمال snapshot روی state لیست
 * @param {{ skipIfSearch?: boolean }} options - اگر skipIfSearch و location.search پر باشد، restore نکن (مثل Leads)
 */
export function useListReturnState(capture, applyRestored, options = {}) {
  const { skipIfSearch = false } = options;
  const location = useLocation();
  const navigate = useNavigate();
  const restoredRef = useRef(false);
  const captureRef = useRef(capture);
  captureRef.current = capture;

  useEffect(() => {
    if (restoredRef.current) return;
    if (skipIfSearch && location.search) return;
    const restored =
      location.state?.[LIST_RESTORE_STATE_KEY] ?? location.state?.listState ?? null;
    if (!restored) return;
    restoredRef.current = true;
    applyRestored(restored);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    // فقط یک‌بار هنگام mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * @param {string} path
   * @param {object} [extraState]
   * @param {{ listPath: string, listLabel: string, entityLabel?: string }} [breadcrumb]
   */
  const navigateToDetail = useCallback(
    (path, extraState = {}, breadcrumb = null) => {
      const snapshot = captureListState(captureRef.current());
      const returnTo = `${location.pathname}${location.search}`;
      if (breadcrumb?.listPath && breadcrumb?.listLabel) {
        navigate(path, {
          state: buildListToDetailNavigationState({
            listSnapshot: snapshot,
            returnTo,
            listPath: breadcrumb.listPath,
            listLabel: breadcrumb.listLabel,
            entityPath: path,
            entityLabel: breadcrumb.entityLabel || '—',
            extraState,
          }),
        });
        return;
      }
      navigate(path, {
        state: buildDetailNavigationState(snapshot, returnTo, extraState),
      });
    },
    [navigate, location.pathname, location.search],
  );

  const getReturnPath = useCallback(
    () => `${location.pathname}${location.search}`,
    [location.pathname, location.search],
  );

  return { navigateToDetail, getReturnPath };
}

/**
 * در صفحات جزئیات: نگه داشتن returnTo و snapshot در ref تا با تغییر tab از بین نرود.
 */
export function useDetailReturnRefs() {
  const location = useLocation();
  const listStateRef = useRef(
    location.state?.listState ?? location.state?.[LIST_RESTORE_STATE_KEY] ?? null,
  );
  const returnToRef = useRef(location.state?.returnTo || null);

  useEffect(() => {
    const nextList =
      location.state?.listState ?? location.state?.[LIST_RESTORE_STATE_KEY] ?? null;
    const nextReturn = location.state?.returnTo || null;
    if (nextList) listStateRef.current = nextList;
    if (nextReturn) returnToRef.current = nextReturn;
  }, [location.state]);

  const buildGoBackState = useCallback(() => {
    const snapshot = listStateRef.current;
    return snapshot
      ? { [LIST_RESTORE_STATE_KEY]: snapshot, listState: snapshot }
      : {};
  }, []);

  return { listStateRef, returnToRef, buildGoBackState };
}
